import { useState } from 'react';
import { Share2, Vote, Minus, Plus, Check, PhoneCall, Info, Image as ImageIcon } from 'lucide-react';
import PhotoUpdateModal from './PhotoUpdateModal';
import PosterModal from './PosterModal';

const USSD_EXT   = process.env.REACT_APP_USSD_EXT || '';
const API_BASE   = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

export default function NomineeCard({ nominee, onVote }) {
  const [votes, setVotes]           = useState(1);
  const [copied, setCopied]         = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);

  const initial = (nominee.name || '?').trim().charAt(0).toUpperCase();

  const handleShare = async () => {
    const url = `${window.location.origin}/vote/${nominee.votingCode}`;
    try {
      if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
        await navigator.share({ title: nominee.name, text: `Vote for ${nominee.name} — code ${nominee.votingCode}`, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* cancelled */
    }
  };

  const adjust = (delta) =>
    setVotes((v) => Math.max(1, Math.min(99999, (parseInt(v, 10) || 1) + delta)));

  return (
    <>
    <article className="nominee-card h-100 d-flex flex-column">
      <div className="nominee-card__media">
        {nominee.photo ? (
          <img
            src={nominee.photo?.startsWith('http') ? nominee.photo : `${API_BASE}/${nominee.photo}`}
            alt={nominee.name}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="nominee-card__placeholder">
            <span className="font-display fw-bold">{initial}</span>
          </div>
        )}

        <span className="vote-code-badge nominee-card__code">
          {nominee.votingCode}
        </span>

        <button
          type="button"
          className="nominee-card__info-btn"
          onClick={() => setShowPhotoModal(true)}
          aria-label="Update your nominee photo"
          title="Are you this nominee? Update your photo"
        >
          <Info size={12} />
          <span className="nominee-card__info-label">update</span>
        </button>

        <span className="nominee-card__category">{nominee.categoryName}</span>
      </div>

      <div className="nominee-card__body p-3 d-flex flex-column gap-3 flex-grow-1">
        <div>
          <p className="card-meta-label">Vote for</p>
          <h3 className="font-display fs-5 fw-semibold mb-0 lh-sm">
            {nominee.name}
          </h3>
        </div>

        <div>
          <p className="card-meta-label card-meta-label--spaced">Enter Number of Votes</p>
          <div className="input-group input-group-sm vote-stepper">
          <button type="button" className="btn btn-outline-secondary" onClick={() => adjust(-1)} aria-label="Decrease votes">
            <Minus size={14} />
          </button>
          <input
            type="number"
            min={1}
            value={votes}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setVotes(Number.isFinite(v) && v >= 1 ? v : 1);
            }}
            className="form-control text-center font-mono fw-bold"
            aria-label="Number of votes"
          />
          <button type="button" className="btn btn-outline-secondary" onClick={() => adjust(1)} aria-label="Increase votes">
            <Plus size={14} />
          </button>
          </div>
        </div>

        <div className="mt-auto d-flex gap-2">
          <button
            type="button"
            onClick={() => onVote(nominee, votes)}
            className="btn gold-chip flex-grow-1 fw-semibold d-inline-flex align-items-center justify-content-center"
          >
            <Vote size={16} className="me-2" /> Vote
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
            aria-label="Share nominee"
            title={copied ? 'Copied!' : 'Share voting link'}
          >
            {copied ? <Check size={14} className="text-success" /> : <Share2 size={14} />}
            <span className="share-btn-label">
              {copied ? 'Copied!' : 'Share Voting Link'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setShowPosterModal(true)}
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
            aria-label="Generate poster"
            title="Generate your poster"
          >
            <ImageIcon size={14} />
          </button>
        </div>

        {copied && (
          <p className="text-success small fw-medium text-center mb-0 mt-n1">
            Link copied to clipboard
          </p>
        )}

        {USSD_EXT && (
          <div className="ussd-hint">
            <PhoneCall size={11} className="ussd-hint__icon" />
            <span>
              To vote offline: Dial <strong>{USSD_EXT}</strong> &middot; Enter code <strong className="font-mono">{nominee.votingCode}</strong> &middot; Follow prompts
            </span>
          </div>
        )}
      </div>
    </article>

    <PhotoUpdateModal
      open={showPhotoModal}
      onClose={() => setShowPhotoModal(false)}
      nominee={nominee}
    />

    <PosterModal
      open={showPosterModal}
      onClose={() => setShowPosterModal(false)}
      nominee={nominee}
    />
    </>
  );
}
